import { useQuery } from '@tanstack/react-query';
import { getPublishedQuizzes } from '../services/quizService';
import { anchor } from '../utils/serverClock';
import { QuizListResponse, QuizSummary } from '../types/quiz';
import { isLiveQuizPreStart, isLiveQuizRunning } from '../utils/liveQuizStatus';

/** Matches the key `useQuizRsvp` invalidates on settle. */
export const STUDENT_QUIZZES_QUERY_KEY = ['quizzes', 'published'] as const;

/**
 * Poll cadence, derived from what the list itself says is happening —
 * fast while a room is actually running (a scoreboard update is worth
 * seeing promptly), slower while something is merely upcoming, off when
 * there's nothing live to watch. Pure and exported so the cadence rules
 * are a data comparison to test, not something to infer from mocked timer
 * behaviour.
 */
export function getQuizzesRefetchInterval(quizzes: QuizSummary[]): number | false {
  const liveQuizzes = quizzes.map(quiz => quiz.live).filter((live): live is NonNullable<typeof live> => !!live);

  if (liveQuizzes.some(isLiveQuizRunning)) {
    return 10_000;
  }
  if (liveQuizzes.some(isLiveQuizPreStart)) {
    return 30_000;
  }
  return false;
}

/**
 * The published quiz list, shared by both tabs of `/student/quizzes` (and
 * anything else that reads `['quizzes','published']`, such as the RSVP
 * mutation's invalidation). Fetched at a generous page size rather than
 * paginated — the live/self-paced split and the search box both filter
 * client-side over the full list.
 */
export function useStudentQuizzes() {
  return useQuery<QuizListResponse>({
    queryKey: STUDENT_QUIZZES_QUERY_KEY,
    queryFn: async () => {
      const response = await getPublishedQuizzes(1, 100);
      // Anchor before returning so every consumer's first render already
      // agrees with the server's clock.
      anchor(response.serverNow);
      return response;
    },
    refetchOnWindowFocus: true,
    refetchInterval: query => getQuizzesRefetchInterval(query.state.data?.quizzes ?? [])
  });
}
